import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import formatError from "utils/formatError";

//Styles
import Styles from "./table.module.scss";

//Assets
import IGear from "assets/i-gear.js";
import IUserStar from "assets/i-user-star.js";
import IInfo from "assets/i-info.js";
import ICopy from "assets/i-copy.js";
import IRumah123 from "assets/i-rumah123.png";
import ILamudi from "assets/i-lamudi.png";
import IBeliRumah from "assets/i-beli-rumah.png";
import IDekoruma from "assets/i-dekoruma.png";
import IVerifiedIcon from "assets/verified-icon.png";
import ICopyV2 from "assets/i-copy-v2.js";

//Components
import ToggleSwitchRounded from "components/toggle-switch-rounded";
import DetailMarketing from "../detail-marketing/DetailMarketing";
import Loader from "components/molecules/Loader";
import ModalLoader from "components/organism/ModalLogout";
import ModalHubungkanAkun from "pages/dashboard/marketing/components/modal-hubungkan-akun/modal-hubungkan-akun.js";
import ModalFormHubungkanAkun from "pages/dashboard/marketing/components/modal-form-hubungkan-akun/modal-form-hubungkan-akun.js";
import ModalResultHubungkanAkun from "pages/dashboard/marketing/components/modal-result-hubungkan-akun/modal-result-hubungkan-akun.js";

//Context
import { useGlobalCtx } from "contexts";
import { useDetailListingState } from "pages/dashboard/marketing/context";
import {
  useUpdateHubungkanAkunLamudi,
  useUpdateHubungkanAkunRumah123,
  useUpdateHubungkanAkunBeliRumahCo,
  useDeleteAkunHubungkan,
} from "api/marketing/mutation";
import { useGetListAkunHubungkan } from "api/marketing/query";

//Actions
import { updateColleague } from "actions";
import useOutsideAlerter from "hooks/useOutsideAlerter";

const Row = ({ data, index, setOpenModalLoader, lastElementRef }) => {
  const { setShowLoading } = useGlobalCtx();
  const ref = useRef(null);

  const {
    image,
    name,
    role,
    email,
    phone,
    registrationNumber,
    no,
    isActive,
    id,
    flagging_portal,
    dekoruma_voucher,
    license_user,
  } = useMemo(() => {
    const dataFlaggingPortal = data?.flagging_portal;

    const flagging_portal = [
      {
        icon: IRumah123,
        name: "rumah123",
        is_active: dataFlaggingPortal?.is_rumah123,
      },
      {
        icon: ILamudi,
        name: "Lamudi",
        is_active: dataFlaggingPortal?.is_lamudi,
      },
      {
        icon: IBeliRumah,
        name: "BeliRumah.co",
        is_active: dataFlaggingPortal?.is_belirumah,
      },
    ];

    return {
      image: data?.user_image?.public?.url ?? "",
      name: data?.full_name ?? "",
      role: data?.role?.name ?? "",
      email: data?.email ?? "",
      phone: data?.phone ?? "",
      registrationNumber: data?.registration_number ?? "",
      no: index + 1,
      isActive: !data?.suspended_at,
      id: data?.id,
      flagging_portal,
      dekoruma_voucher: data?.dekoruma_voucher,
      license_user: data?.license_user,
    };
  }, [data, index]);

  const hasFlaggingPortal = flagging_portal?.some((item) => item?.is_active);

  const [status, setStatus] = useState(isActive);
  const [selectedAkun, setSelectedAkun] = useState(null);
  const [openModal, setOpenModal] = useState(""); // detail | hubungkan-akun | form-hubungkan-akun | hasil-hubungkan-akun
  const [openInternalLoader, setOpenInternalLoader] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isGagal, setIsGagal] = useState(false);

  const {
    mutate: updateHubungkanAkunLamudiMutation,
    isLoading: isLoadingUpdateHubungkanAkunLamudiMutation,
  } = useUpdateHubungkanAkunLamudi();
  const {
    mutate: updateHubungkanAkunRumah123Mutation,
    isLoading: isLoadingUpdateHubungkanAkunRumah123Mutation,
  } = useUpdateHubungkanAkunRumah123();
  const {
    mutate: updateHubungkanAkunBeliRumahCoMutation,
    isLoading: isLoadingUpdateHubungkanAkunBeliRumahCoMutation,
  } = useUpdateHubungkanAkunBeliRumahCo();
  const { mutate: deleteAkunHubungkanMutation } = useDeleteAkunHubungkan();
  const {
    data: listAkunHubungkanData,
    refetch,
    isLoading: isLoadingListAkunHubungkan,
  } = useGetListAkunHubungkan({
    params: { user_id: id },
    enabled: openModal === "hubungkan-akun",
  });

  const onCloseModal = () => {
    setOpenModal("");
    setSelectedAkun(null);
  };

  const hitActiveEnpoint = async () => {
    try {
      setOpenInternalLoader(true);
      const formData = new FormData();
      formData.append(
        `user[suspended_at]`,
        status ? dayjs().format("YYYY-MM-DD hh:mm:ss") : null,
      );

      const response = await updateColleague(id, formData);

      setStatus(!response?.data?.suspended_at);
    } catch (error) {
      console.log(error);
    } finally {
      setOpenInternalLoader(false);
    }
  };

  const onHubungkanAkun = (email) => {
    const api =
      selectedAkun.name === "Lamudi"
        ? updateHubungkanAkunLamudiMutation
        : selectedAkun.name === "rumah123"
          ? updateHubungkanAkunRumah123Mutation
          : selectedAkun.name === "BeliRumah.co"
            ? updateHubungkanAkunBeliRumahCoMutation
            : null;

    api(
      { data: { email }, user_id: id },
      {
        onSuccess: () => {
          toast.success("Akun berhasil dihubungkan");
          setOpenModal("hasil-hubungkan-akun");
          refetch();
        },
        onError: (error) => {
          toast.error(formatError(error));
          if (selectedAkun.name === "BeliRumah.co") {
            setIsGagal(true);
            setOpenModal("hasil-hubungkan-akun");
          } else {
            setIsGagal(false);
          }
        },
      },
    );
  };

  const onDeleteAkunHubungkan = (akun) => {
    let data = {};

    switch (akun.name) {
      case "Lamudi":
        data = {
          agent_lamudi: true,
        };
        break;
      case "rumah123":
        data = {
          agent_rumah123: true,
        };
        break;
      case "BeliRumah.co":
        data = {
          agent_belirumah: true,
        };
        break;
      default:
        break;
    }

    deleteAkunHubungkanMutation(
      { data, user_id: id },
      {
        onSuccess: () => {
          toast.success("Akun berhasil dihapus");
          onCloseModal();
          refetch();
        },
        onError: (error) => {
          toast.error(formatError(error));
        },
      },
    );
  };

  const onCopy = () => {
    navigator.clipboard.writeText(dekoruma_voucher);
    toast.success("Nomor berhasil disalin");
  };
  
  const onCopyLicenseUser = () => {
    navigator.clipboard.writeText(license_user);
    toast.success("Nomor berhasil disalin");
  };

  useEffect(() => {
    setStatus(isActive);
  }, [isActive]);

  useEffect(() => {
    const _isLoading =
      isLoadingUpdateHubungkanAkunLamudiMutation ||
      isLoadingUpdateHubungkanAkunRumah123Mutation ||
      isLoadingUpdateHubungkanAkunBeliRumahCoMutation;

    setShowLoading(_isLoading);
  }, [
    isLoadingUpdateHubungkanAkunLamudiMutation,
    isLoadingUpdateHubungkanAkunRumah123Mutation,
    isLoadingUpdateHubungkanAkunBeliRumahCoMutation,
    setShowLoading,
  ]);

  useOutsideAlerter(ref, () => setIsOpenDropdown(false));

  return (
    <div
      ref={lastElementRef}
      className="py-[32px] border-b border-solid border-[#f5f5f5]"
    >
      <div className={Styles["row"]}>
        <div className={`${Styles["col"]} ${Styles["number"]}`}>{no}</div>
        <div className={`${Styles["col"]} ${Styles["image"]}`}>
          <img src={image} alt={name} />
        </div>

        <div className="w-full">
          <div className="grid grid-cols-6 gap-[40px]">
            <div
              className={`${Styles["col"]} ${Styles["double"]} ${Styles["name"]}`}
            >
              <p className={`${Styles["first"]}`}>{name}</p>
              <p className={`${Styles["last"]}`}>{role}</p>
            </div>
            <div
              className={`${Styles["col"]} ${Styles["double"]} ${Styles["email"]}`}
            >
              <p className={`${Styles["first"]}`}>{email}</p>
              <p className={`${Styles["last"]}`}>email</p>
            </div>
            <div
              className={`${Styles["col"]} ${Styles["double"]} ${Styles["telepon"]}`}
            >
              <p className={`${Styles["first"]}`}>{phone}</p>
              <p className={`${Styles["last"]}`}>telepon</p>
            </div>
            <div
              className={`${Styles["col"]} ${Styles["double"]} ${Styles["no-registrasi"]}`}
            >
              <p className={`${Styles["first"]}`}>{registrationNumber}</p>
              <p className={`${Styles["last"]}`}>No. Registrasi</p>
            </div>
            <div className={`${Styles["col"]} ${Styles["active"]}`}>
              <ToggleSwitchRounded active={status} toggle={hitActiveEnpoint} />
              <p className={Styles["active-text"]}>Suspend</p>
            </div>
            <div className={`${Styles["col"]} ${Styles["info"]}`}>
              <div className={Styles["info-wrapper"] + " relative"}>
                <button onClick={() => setIsOpenDropdown(true)}>
                  <IGear />
                </button>

                {isOpenDropdown && (
                  <div
                    ref={ref}
                    className={
                      "absolute z-10 right-0 top-[40px] w-[230px] rounded-[6px] border border-solid border-[#D8D8D9] bg-white shadow-[0_2px_4px_0_rgba(16,16,16,0.16)]"
                    }
                  >
                    <div
                      onClick={() => setOpenModal("detail")}
                      className="flex items-center gap-[12px] py-2 px-[12px] rounded-t-[6px] cursor-pointer hover:bg-[#E6E7E8]"
                    >
                      <IInfo />
                      <p>Detail Marketing</p>
                    </div>
                    <div
                      onClick={() => setOpenModal("hubungkan-akun")}
                      className="flex items-center gap-[12px] py-2 px-[12px] rounded-b-[6px] cursor-pointer hover:bg-[#E6E7E8]"
                    >
                      <IUserStar />
                      <p>Akun Terhubung</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {license_user && (
            <div className="pt-[6px] mt-[6px]">
              <div onClick={() => onCopyLicenseUser(license_user)} className="flex items-center bg-[linear-gradient(90deg,#ebf0f700_0%,#EBF0F7_100%)] w-full">
                <img src={IVerifiedIcon} alt="verified" className="w-[40px] h-[40px]" />
                <p className="text-[13px] font-inter-semibold text-[#252526] mr-2">{license_user}</p>
                <ICopyV2 width={16} height={16} />
              </div>
              <p className="text-[11px] font-inter text-[#686A68]">Tenaga Pemasar Properti Tersertifikasi oleh BNSP</p>
            </div>
          )}

          {(hasFlaggingPortal || !!dekoruma_voucher) && (
            <div className="grid grid-cols-2 gap-[12px] border-t border-dashed border-[#808285] pt-[6px] mt-[6px]">
              {hasFlaggingPortal && (
                <div className="flex items-center gap-[12px]">
                  <p className="text-[#808285] text-[11px]">
                    Akun ini terhubung dengan
                  </p>
                  {flagging_portal?.map((item) => {
                    if (item?.is_active) {
                      return (
                        <img
                          className="w-[52px] h-[24px] object-contain"
                          key={item?.name}
                          src={item?.icon}
                          alt={item?.name}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              )}
             {!!dekoruma_voucher && <div className="flex items-center gap-[12px] justify-end">
                <img
                  className="w-[52px] h-[24px] object-contain"
                  src={IDekoruma}
                  alt="dekoruma"
                />

                <button
                  onClick={onCopy}
                  className="rounded-[2px] border-[0.5px] border-solid border-[#DEDFDE] bg-[#FAFAFA] flex items-center gap-[12px]"
                >
                  {dekoruma_voucher}
                  <ICopy />
                </button>
              </div>}
            </div>
          )}
        </div>
      </div>

      {openInternalLoader && <ModalLoader open={openInternalLoader} />}
      {openModal === "detail" && (
        <DetailMarketing
          setOpenModalLoader={setOpenModalLoader}
          key={`colleague-tables-${index}-${id}`}
          onClose={() => setOpenModal(false)}
          data={data}
        />
      )}
      <ModalHubungkanAkun
        onSelect={(value) => {
          setSelectedAkun(value);
          setOpenModal("form-hubungkan-akun");
        }}
        onDelete={(value) => {
          onDeleteAkunHubungkan(value);
        }}
        onClose={onCloseModal}
        isOpen={openModal === "hubungkan-akun"}
        listAkunHubungkanData={listAkunHubungkanData?.data?.data}
        isLoadingListAkunHubungkan={isLoadingListAkunHubungkan}
      />
      <ModalFormHubungkanAkun
        onSubmit={(email) => onHubungkanAkun(email)}
        onClose={onCloseModal}
        isOpen={openModal === "form-hubungkan-akun"}
        akun={selectedAkun}
        onBack={() => setOpenModal("hubungkan-akun")}
      />
      <ModalResultHubungkanAkun
        onSubmit={(email) => onHubungkanAkun(email)}
        onClose={onCloseModal}
        isOpen={openModal === "hasil-hubungkan-akun"}
        akun={selectedAkun}
        onBack={() => setOpenModal("hubungkan-akun")}
        isGagal={isGagal}
      />
    </div>
  );
};

const Table = () => {
  const {
    colleagues,
    setOpenModalLoader,
    loading,
    triggeredInfiniteScroll,
    hasMore,
    page,
  } = useDetailListingState();

  // Infinitescroll Observer
  const observer = useRef();

  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          triggeredInfiniteScroll();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  return (
    <div className={Styles["wrapper"]}>
      {!!colleagues.length &&
        colleagues.map((colleague, index) =>
          colleagues.length === index + 1 ? (
            <Row
              lastElementRef={lastElementRef}
              setOpenModalLoader={setOpenModalLoader}
              key={`colleague-${index}`}
              index={index}
              data={colleague}
            />
          ) : (
            <Row
              setOpenModalLoader={setOpenModalLoader}
              key={`colleague-${index}`}
              index={index}
              data={colleague}
            />
          ),
        )}

      {!!(page > 1 && loading) && (
        <div className={Styles["centered"]}>
          <Loader />
        </div>
      )}
    </div>
  );
};

export default Table;
